import type { ConfiguratorConfig, ConfiguratorUnavailablePayload, PricingRequest, PricingResponse } from './types';
import { isConfiguratorUnavailablePayload } from './types';

/**
 * Thin fetch wrapper for the plugin's luma/v1 REST endpoints.
 *
 * All requests include the WordPress REST nonce for authentication.
 * The base REST URL and nonce are read from the bootstrapped data passed
 * down from the PHP template.
 */
export class ApiService {

	private readonly restUrl: string;
	private readonly restNonce: string;

	constructor(restUrl: string, restNonce: string) {
		this.restUrl    = restUrl.replace(/\/$/, '');
		this.restNonce  = restNonce;
	}

	async fetchConfig(productId: number): Promise<ConfiguratorConfig | ConfiguratorUnavailablePayload> {
		const url = `${this.restUrl}/configurator/config?product_id=${productId}`;
		const data  = await this.get<unknown>(url);

		if (isConfiguratorUnavailablePayload(data)) {
			return data;
		}

		return data as ConfiguratorConfig;
	}

	async fetchPrice(request: PricingRequest): Promise<PricingResponse> {
		return this.post<PricingResponse>(`${this.restUrl}/configurator/price`, request);
	}

	private async get<T>(url: string): Promise<T> {
		const response = await fetch(url, {
			headers: {
				'X-WP-Nonce': this.restNonce,
			},
		});

		return this.handleResponse<T>(response);
	}

	private async post<T>(url: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': this.restNonce,
				...extraHeaders,
			},
			body: JSON.stringify(body),
		});

		return this.handleResponse<T>(response);
	}

	private async handleResponse<T>(response: Response): Promise<T> {
		const data = await response.json() as unknown;

		if (!response.ok) {
			const error = data as { message?: string };
			throw new Error(error.message ?? `Request failed (${response.status})`);
		}

		return data as T;
	}
}
